import { useState, useRef, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Label } from '../../../components/common/FormLabel';
import { Button } from '../../../components/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/shadcn/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FilterType, FilterViewModel } from '../../../types/domain';
import { SelectOption } from '../../../components/common/Dropdown/types';
import { MultiSelectFilter, DropdownFilter, TextBoxFilter, DateRangeFilter } from './filters';

type DynamicFiltersSectionProps = {
  hasValidationError?: boolean;
  onValidationErrorHandled?: () => void;
  availableFilters: FilterViewModel[];
  multiSelectSelectedOptions?: Record<string, SelectOption[]>;
  onMultiSelectSelectedOptionsChange?: (filterId: string, selectedOptions: SelectOption[]) => void;
};

export const DynamicFiltersSection = ({ hasValidationError, onValidationErrorHandled, availableFilters = [], multiSelectSelectedOptions = {}, onMultiSelectSelectedOptionsChange }: DynamicFiltersSectionProps) => {
  const { control, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dynamicFilters'
  });
  const [addFilterValue, setAddFilterValue] = useState<string | undefined>(undefined);
  
  const selectedFilters = fields.map((field: any) => field.filterId);

  // Refs to filter containers so we can focus/scroll to them
  const filterRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [highlightedFilter, setHighlightedFilter] = useState<string | null>(null);

  // When parent signals validation errors, focus the first invalid filter
  useEffect(() => {
    if (!hasValidationError) return;

    // Find first dynamic filter with an error in react-hook-form
    const errors = (formState as any)?.errors || {};
    let firstInvalidFilterId: string | null = null;

    // Check dynamicFilters errors (they will be at dynamicFilters.<idx>.values)
    if (errors && errors.dynamicFilters) {
      const arr = errors.dynamicFilters as any[];
      for (let i = 0; i < (arr || []).length; i++) {
        if (arr[i]) {
          const df = fields[i] as any;
          if (df && df.filterId) {
            firstInvalidFilterId = df.filterId;
            break;
          }
        }
      }
    }

    // Scroll to and highlight the first invalid filter
    if (firstInvalidFilterId) {
      const el = filterRefs.current[firstInvalidFilterId];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedFilter(firstInvalidFilterId);
        setTimeout(() => setHighlightedFilter(null), 2500);
      }
    }

    if (onValidationErrorHandled) onValidationErrorHandled();

  }, [hasValidationError]);

  const addFilter = (filterId: string) => {
    const filter = availableFilters.find(f => f.id === filterId);
    if (!filter || selectedFilters.includes(filterId)) return;

    append({
      filterId,
      value: getDefaultValueForFilter(filter.filterType)
    });
    
    // reset the add-filter select back to placeholder
    setAddFilterValue(undefined);
  };

  const removeFilter = (index: number) => {
    const field = fields[index] as any;
    if (field && field.filterId && onMultiSelectSelectedOptionsChange) {
      // Clean up selectedOptions for multiselect filters when removed
      const filter = availableFilters.find(f => f.id === field.filterId);
      if (filter && filter.filterType === FilterType.MultiSelect) {
        onMultiSelectSelectedOptionsChange(field.filterId, []);
      }
    }
    remove(index);
  };

  const getDefaultValueForFilter = (filterType: FilterType) => {
    switch (filterType) {
      case FilterType.MultiSelect:
        return [];
      case FilterType.DropDown:
      case FilterType.TextBox:
        return [''];
      case FilterType.DateRange:
        return ['', ''];
      default:
        return [''];
    }
  };

  const renderFilterInput = (filter: FilterViewModel, fieldIndex: number) => {
    const fieldName = `dynamicFilters.${fieldIndex}.values` as const;
    const filterType = typeof filter.filterType === 'number' ? filter.filterType : Number(filter.filterType);
    
    switch (filterType) {
      case FilterType.MultiSelect:
        return (
          <MultiSelectFilter 
            filter={filter} 
            fieldName={fieldName} 
            control={control}
            selectedOptions={multiSelectSelectedOptions[filter.id] || []}
            onSelectedOptionsChange={(selectedOptions: SelectOption[]) => {
              if (onMultiSelectSelectedOptionsChange) {
                onMultiSelectSelectedOptionsChange(filter.id, selectedOptions);
              }
            }}
          />
        );

      case FilterType.DropDown:
        return <DropdownFilter filter={filter} fieldName={fieldName} control={control} />;

      case FilterType.TextBox:
        return <TextBoxFilter filter={filter} fieldName={fieldName} control={control} />;

      case FilterType.DateRange:
        return <DateRangeFilter fieldName={fieldName} control={control} />;

      default:
        return <div>Unsupported filter type</div>;
    }
  };

  return (
    <div>
      <Label forField="dynamicFilters" label="Filters" />
      <p className="text-sm text-gray-500 mt-1 mb-3">
        Only include results that match the following filters:
      </p>

      {/* Add Filter Dropdown */}
      <div className="mb-4">
        <Select value={addFilterValue || ''} onValueChange={(v) => { if (v) { addFilter(v); } /* reset to placeholder */ setAddFilterValue(''); }}>
          <SelectTrigger className="w-full md:max-w-sm">
            <SelectValue placeholder="Add a filter" />
          </SelectTrigger>
          <SelectContent>
            {availableFilters
              .filter(filter => !selectedFilters.includes(filter.id))
              .map((filter) => (
                <SelectItem key={filter.id} value={filter.id}>
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  {filter.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selected Filters */}
      <div className="space-y-4">
        {availableFilters.length > 0 && fields.map((field: any, index) => {
          const filter = availableFilters.find(f => f.id === field.filterId);
          
          if (!filter) return null;

          return (
            <div
              key={field.id}
              ref={(el) => { filterRefs.current[field.filterId] = el; }}
              className={"border rounded-lg p-4 bg-gray-50 " + (highlightedFilter === field.filterId ? 'ring-2 ring-red-400 bg-red-50' : '')}
            >
              <div className="flex justify-between items-center">
                <Label forField={`filter-${field.filterId}`} label={filter.name} />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFilter(index)}
                  className="text-gray-700 hover:text-red-700"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
              
              <div className="mt-4">
                {renderFilterInput(filter, index)}
              </div>
            </div>
          );
        })}
      </div>

      {fields.length === 0 && (
        <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
          <p>No filters added yet.</p>
          <p className="text-sm">Use the dropdown above to add dynamic filters.</p>
        </div>
      )}
    </div>
  );
};
